import { Mail, Globe } from 'lucide-react';
import { ParticipantTabletopState } from '../../../services/participant.service';

interface Props {
  tabletop: ParticipantTabletopState;
}

function formatSimTime(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `T+${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// Injects are authored with a leading "LABEL: body" convention (e.g. "GOLD
// COMMAND CONVENED: ...", "AUTOMATED ALERT: ..."), but that's just plain
// text today — pulling the label out into its own line makes a dense wall
// of scenario text actually scannable instead of every card looking
// identical until you read the whole thing.
const LABEL_PREFIX = /^([A-Z][A-Z0-9 /&-]{3,60}):\s*/;

function splitLabel(content: string): { label: string | null; body: string } {
  const match = content.match(LABEL_PREFIX);
  if (!match) return { label: null, body: content };
  return { label: match[1], body: content.slice(match[0].length) };
}

// Private injects, newest first, sim-time stamped (plan 6.3). Public
// artefacts render below — "public artefacts render on both surfaces" per
// the same plan section; the second screen shows them in PublicWorldWidget,
// this is the participant-device equivalent surface.
export function InboxTab({ tabletop }: Props) {
  if (!tabletop.seat) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <p className="text-[var(--pt-text-dimmer)] text-sm text-center">Claim a seat in the Brief tab to see your inbox.</p>
      </div>
    );
  }

  const hasPublicArtefacts = tabletop.publicArtefacts.length > 0;

  return (
    <div className={`flex-1 overflow-y-auto p-4 lg:p-0 flex flex-col gap-4 ${hasPublicArtefacts ? 'lg:grid lg:grid-cols-2 lg:items-start' : ''}`}>
      <div>
        <p className="text-[var(--pt-text-dimmer)] text-xs uppercase tracking-wide font-medium mb-2 flex items-center gap-1.5">
          <Mail size={12} /> Your inbox
        </p>
        {tabletop.inbox.length === 0 ? (
          <div className="bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl p-6 text-center">
            <p className="text-[var(--pt-text-dimmer)] text-sm">Nothing addressed to you yet.</p>
          </div>
        ) : (
          <div className={`flex flex-col gap-2 ${hasPublicArtefacts ? '' : 'lg:grid lg:grid-cols-2 lg:items-start lg:gap-3'}`}>
            {tabletop.inbox.map(item => {
              const { label, body } = splitLabel(item.content);
              return (
                <div key={item.seq} className="bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl p-3">
                  <div className="flex items-center justify-between gap-2">
                    {label && <p className="text-primary text-[11px] font-bold uppercase tracking-wide">{label}</p>}
                    <p className="text-[var(--pt-text-dimmer)] text-[11px] font-mono ml-auto flex-shrink-0">{formatSimTime(item.simTimeMs)}</p>
                  </div>
                  <p className="text-[var(--pt-text)] text-sm mt-1 leading-snug">{body}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {hasPublicArtefacts && (
        <div>
          <p className="text-[var(--pt-text-dimmer)] text-xs uppercase tracking-wide font-medium mb-2 flex items-center gap-1.5">
            <Globe size={12} /> Public updates
          </p>
          <div className="flex flex-col gap-2 lg:grid lg:grid-cols-2 lg:gap-3">
            {tabletop.publicArtefacts.map(a => (
              <div key={a.artefactId} className="bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl overflow-hidden">
                {a.url && <img src={a.url} alt={a.text || a.kind} className="w-full h-32 object-cover" />}
                <div className="p-3">
                  <p className="text-[var(--pt-text-dimmer)] text-[10px] uppercase tracking-wide">{a.kind.replace(/_/g, ' ')}</p>
                  <p className="text-[var(--pt-text)] text-sm mt-0.5">{a.lowerThird || a.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
